import { useEffect } from "react";
import { type PossibleMetadata, mergeTitles } from "shared/util/metadata";
import { redirect } from "shared/util/navigation";
import { MetadataContext, useMetadata } from "../context/MetadataContext.js";
import { useOptionalNavigator } from "./Navigator.js";

/**
 * Component for a single page (or screen) within an app.
 * - Changes the document title (that appears in the page tab) on render.
 * - Must appear inside an `<App />` or `<Html />` component or won't be able to access their .
 */
export function Page({ children, status = 200, path: vanityPath, ...metadata }: { children: React.ReactNode } & PossibleMetadata): React.ReactElement {
	const navigatorPath = useOptionalNavigator()?.value;
	return (
		<MetadataContext
			{...metadata}
			status={status}
			path={vanityPath || navigatorPath} // Use vanity path if it's set, or fall back to router path.
		>
			<PageEffect />
			<main aria-label="Page">{children}</main>
		</MetadataContext>
	);
}

/** Effect that updates the current title in the browser when a `<Page>` component is attached to the DOM. */
function PageEffect(): null {
	const { status, path, title, site } = useMetadata();
	useEffect(() => {
		if (status && status >= 200 && path) redirect(path, mergeTitles(title, site));
	}, [status, path, title, site]);
	return null;
}
